import { Redirect, Route, Switch } from "wouter";
import { Layout } from "./components/layout";
import { useAuth } from "./lib/auth-context";
import { ChromiumMajorDetail } from "./pages/ChromiumMajorDetail";
import { ChromiumReleases } from "./pages/ChromiumReleases";
import { ClusterRouting, ClusterSecurity } from "./pages/cluster";
import { Dashboard } from "./pages/Dashboard";
import { Login } from "./pages/Login";
import { Manifest } from "./pages/Manifest";
import { NotFound } from "./pages/NotFound";
import { OperationDetail } from "./pages/OperationDetail";
import { Operations } from "./pages/Operations";
import { ProfileReleaseDetail } from "./pages/ProfileReleaseDetail";
import { ProfilesReleases } from "./pages/ProfilesReleases";
import { ReleaseNewChromium } from "./pages/ReleaseNewChromium";
import { ReleaseNewProfiles } from "./pages/ReleaseNewProfiles";
import { ReleasesOverview } from "./pages/ReleasesOverview";
import { Sessions } from "./pages/Sessions";
import { SessionViewer } from "./pages/SessionViewer";
import { Settings } from "./pages/Settings";
import { SetupWizard } from "./pages/setup";
import { useSetupStatus } from "./pages/setup/use-setup-status";
import { Updates } from "./pages/Updates";
import { Uploads } from "./pages/Uploads";
import { Workers } from "./pages/Workers";
import { WorkersHistory } from "./pages/WorkersHistory";
import { AddWorkerPage } from "./pages/workers/new";

export function App() {
	const { user, isLoading } = useAuth();
	const setup = useSetupStatus();

	if (isLoading || setup.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
				Loading…
			</div>
		);
	}

	const status = setup.data;

	// First run, no admin yet: hand straight to the wizard's create-admin step
	// (no login screen — there's nothing to log into).
	if (!user) {
		if (status?.needsAdmin) return <SetupWizard />;
		return <Login />;
	}

	// Authed but the first-run wizard hasn't been completed: take over with the
	// wizard until "Review & launch" stamps setupCompletedAt. Gating on the
	// explicit completion marker (not needsRender) keeps the operator in the
	// wizard through the worker + review steps — the TLS step renders mid-flow,
	// which would otherwise flip needsRender and bounce them to the dashboard
	// before the edge is hardened. (Pre-existing clusters are backfilled
	// complete, so upgrades skip straight to the app.)
	if (status && !status.setupComplete) {
		return <SetupWizard />;
	}

	return (
		<Switch>
			{/* Outside the shell on purpose: the viewer is one iframe under one
			    toolbar, and a sidebar next to a remote desktop is just a
			    smaller remote desktop. */}
			<Route path="/sessions/:hostname/:launchId">
				{(params) => (
					<SessionViewer
						hostname={params.hostname}
						launchId={params.launchId}
					/>
				)}
			</Route>
			<Route>
				<AppShell />
			</Route>
		</Switch>
	);
}

function AppShell() {
	return (
		<Layout>
			<Switch>
				<Route path="/" component={Dashboard} />
				<Route path="/sessions" component={Sessions} />
				<Route path="/setup">
					<SetupWizard />
				</Route>
				<Route path="/releases" component={ReleasesOverview} />
				<Route path="/releases/chromium" component={ChromiumReleases} />
				<Route path="/releases/chromium/:major">
					{(params) => <ChromiumMajorDetail major={params.major} />}
				</Route>
				<Route path="/releases/profiles" component={ProfilesReleases} />
				<Route path="/releases/profiles/:releaseId">
					{(params) => <ProfileReleaseDetail releaseId={params.releaseId} />}
				</Route>
				<Route path="/releases/new/profiles" component={ReleaseNewProfiles} />
				<Route path="/releases/new/chromium" component={ReleaseNewChromium} />
				<Route path="/operations" component={Operations} />
				<Route path="/operations/:id">
					{(params) => <OperationDetail id={params.id} />}
				</Route>
				<Route path="/cluster">
					<Redirect to="/cluster/routing" replace />
				</Route>
				<Route path="/cluster/routing" component={ClusterRouting} />
				<Route path="/cluster/security" component={ClusterSecurity} />
				{/* `history` and `new` before `:hostname` so wouter matches the
				    static paths first — a worker cannot be named either (see
				    RESERVED_WORKER_NAMES) but the router still has to try it. */}
				<Route path="/workers">
					<Workers />
				</Route>
				<Route path="/workers/history">
					<WorkersHistory />
				</Route>
				<Route path="/workers/new" component={AddWorkerPage} />
				<Route path="/workers/:hostname">
					{(params) => <Workers hostname={params.hostname} />}
				</Route>
				{/* Bookmarks and muscle memory from when this was /fleet and the
				    pool was a separate page. */}
				<Route path="/fleet">
					<Redirect to="/workers" replace />
				</Route>
				<Route path="/fleet/history">
					<Redirect to="/workers/history" replace />
				</Route>
				<Route path="/fleet/:hostname">
					{(params) => <Redirect to={`/workers/${params.hostname}`} replace />}
				</Route>
				<Route path="/cluster/pool">
					<Redirect to="/workers" replace />
				</Route>
				<Route path="/manifest" component={Manifest} />
				<Route path="/updates" component={Updates} />
				<Route path="/uploads" component={Uploads} />
				<Route path="/settings" component={Settings} />
				<Route>
					<NotFound />
				</Route>
			</Switch>
		</Layout>
	);
}
